import axios from "axios";
import { api, throwApiError } from "../../api/api";

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

export type QuizQuestionType = "SINGLE_CHOICE" | "MULTI_CHOICE" | "TRUE_FALSE";
export type QuizAudience = "ALL_SUPERVISORS" | "SELECTED" | "ALL_EMPLOYEES";
export type QuizCampaignStatus = "AGENDADA" | "ABERTA" | "ENCERRADA";
export type QuizParticipantStatus = "NAO_INICIOU" | "EM_ANDAMENTO" | "ENVIADO";

export type QuizOption = { id?: string; order?: number; label: string; isCorrect: boolean };

export type QuizQuestion = {
  id?: string;
  order?: number;
  prompt: string;
  type: QuizQuestionType;
  weight: number;
  explanation?: string | null;
  options: QuizOption[];
};

export type Quiz = {
  id: string;
  title: string;
  description: string | null;
  archivedAt: string | null;
  createdAt: string;
  locked: boolean;
  questions: QuizQuestion[];
};

export type QuizListItem = Omit<Quiz, "questions"> & { questionCount: number; campaignCount: number };

export type SaveQuizPayload = {
  title: string;
  description?: string;
  questions: Array<{
    prompt: string;
    type: QuizQuestionType;
    weight: number;
    explanation?: string;
    options: Array<{ label: string; isCorrect: boolean }>;
  }>;
};

export type QuizCampaign = {
  id: string;
  quizId: string;
  title: string;
  slug: string;
  audience: QuizAudience;
  opensAt: string;
  closesAt: string;
  closedAt: string | null;
  showAnswers: boolean;
  passingScore: number | null;
  createdAt: string;
  quiz: { id: string; title: string };
  status: QuizCampaignStatus;
  participantCount: number;
  submittedCount: number;
  averageScore: number | null;
  /** Quem encerrou antes do prazo, pelo log de auditoria (ADR 0035). `null` quando fechou pelo prazo ou antes do log. */
  closedBy: { label: string; at: string } | null;
};

export type CreateQuizCampaignPayload = {
  quizId: string;
  title: string;
  opensAt?: string;
  closesAt: string;
  audience: QuizAudience;
  userIds?: string[];
  showAnswers?: boolean;
  passingScore?: number;
};

export type UpdateQuizCampaignPayload = {
  title?: string;
  opensAt?: string;
  closesAt?: string;
  showAnswers?: boolean;
  passingScore?: number | null;
  addUserIds?: string[];
  removeUserIds?: string[];
};

export type QuizSupervisor = { id: string; nomeCompleto: string; userType: string };

export type QuizResultRow = {
  user: QuizSupervisor;
  status: QuizParticipantStatus;
  attemptId: string | null;
  startedAt: string | null;
  submittedAt: string | null;
  score: number | null;
  points: number | null;
  maxPoints: number | null;
  approved: boolean | null;
  resets: Array<{ resetAt: string; reason: string | null; score: number | null }>;
};

export type QuizQuestionStat = {
  id: string;
  order: number;
  prompt: string;
  type: QuizQuestionType;
  weight: number;
  answered: number;
  correct: number;
  correctRate: number | null;
  options: Array<{ id: string; label: string; isCorrect: boolean; chosen: number }>;
};

export type QuizReviewQuestion = {
  id: string;
  order: number;
  prompt: string;
  type: QuizQuestionType;
  weight: number;
  explanation: string | null;
  options: Array<{ id: string; label: string; isCorrect: boolean }>;
  selectedOptionIds: string[];
  isCorrect: boolean | null;
};

export type QuizAttemptDetail = {
  id: string;
  startedAt: string;
  submittedAt: string | null;
  score: number | null;
  points: number | null;
  maxPoints: number | null;
  approved: boolean | null;
  resetAt: string | null;
  resetReason: string | null;
  user: { id: string; nomeCompleto: string };
  questions: QuizReviewQuestion[];
};

// Público ----------------------------------------------------------------

export type QuizPublicInfo = {
  title: string;
  quizTitle: string;
  description: string | null;
  status: QuizCampaignStatus;
  opensAt: string;
  closesAt: string;
  questionCount: number;
};

export type QuizPublicQuestion = {
  id: string;
  order: number;
  prompt: string;
  type: QuizQuestionType;
  options: Array<{ id: string; label: string }>;
};

export type QuizStartResult = {
  attemptToken: string;
  name: string;
  title: string;
  closesAt: string;
  questions: QuizPublicQuestion[];
};

export type QuizSubmitResult = {
  score: number;
  points: number;
  maxPoints: number;
  correctCount: number;
  questionCount: number;
  passingScore: number | null;
  approved: boolean | null;
  answers: QuizReviewQuestion[] | null;
};

/** Erro das rotas públicas com o status, para a tela escolher o estado certo. */
export class QuizPublicError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

const throwPublicError = (error: unknown, fallback: string): never => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as any;
    const msg = Array.isArray(data?.message) ? data.message.join(" | ") : typeof data?.message === "string" ? data.message : fallback;
    throw new QuizPublicError(msg, error.response?.status ?? 0);
  }
  throw new QuizPublicError(fallback, 0);
};

const call = async <T>(fallback: string, fn: () => Promise<{ data: T }>): Promise<T> => {
  try {
    const { data } = await fn();
    return data;
  } catch (error) {
    return throwApiError(error, fallback);
  }
};

// ---------------------------------------------------------------------------
// Cadastro (ADMIN)
// ---------------------------------------------------------------------------

export const listQuizzesApi = () => call<QuizListItem[]>("Erro ao listar os quizzes.", () => api.get("/quizzes"));

export const getQuizApi = (id: string) => call<Quiz>("Erro ao carregar o quiz.", () => api.get(`/quizzes/${id}`));

export const createQuizApi = (payload: SaveQuizPayload) =>
  call<Quiz>("Erro ao criar o quiz.", () => api.post("/quizzes", payload));

export const updateQuizApi = (id: string, payload: SaveQuizPayload) =>
  call<Quiz>("Erro ao salvar o quiz.", () => api.put(`/quizzes/${id}`, payload));

export const duplicateQuizApi = (id: string) =>
  call<Quiz>("Erro ao duplicar o quiz.", () => api.post(`/quizzes/${id}/duplicate`));

export const archiveQuizApi = (id: string) =>
  call<{ ok: boolean }>("Erro ao arquivar o quiz.", () => api.delete(`/quizzes/${id}`));

// ---------------------------------------------------------------------------
// Campanhas (ADMIN)
// ---------------------------------------------------------------------------

export const listQuizCampaignsApi = (quizId?: string) =>
  call<QuizCampaign[]>("Erro ao listar as campanhas.", () => api.get("/quiz-campaigns", { params: quizId ? { quizId } : undefined }));

export const listQuizSupervisorsApi = () =>
  call<QuizSupervisor[]>("Erro ao listar os supervisores.", () => api.get("/quiz-campaigns/supervisors"));

/** Quantos colaboradores do DP entram numa campanha "Colaboradores" publicada agora. */
export const countQuizEmployeesApi = () =>
  call<{ count: number }>("Erro ao contar os colaboradores.", () => api.get("/quiz-campaigns/employees/count"));

export const createQuizCampaignApi = (payload: CreateQuizCampaignPayload) =>
  call<QuizCampaign>("Erro ao publicar o quiz.", () => api.post("/quiz-campaigns", payload));

export const getQuizCampaignApi = (id: string) =>
  call<QuizCampaign>("Erro ao carregar a campanha.", () => api.get(`/quiz-campaigns/${id}`));

export const updateQuizCampaignApi = (id: string, payload: UpdateQuizCampaignPayload) =>
  call<QuizCampaign>("Erro ao salvar a campanha.", () => api.patch(`/quiz-campaigns/${id}`, payload));

export const closeQuizCampaignApi = (id: string) =>
  call<QuizCampaign>("Erro ao encerrar a campanha.", () => api.post(`/quiz-campaigns/${id}/close`));

export const resetQuizAttemptApi = (id: string, userId: string, reason: string) =>
  call<{ ok: boolean }>("Erro ao liberar nova tentativa.", () =>
    api.post(`/quiz-campaigns/${id}/participants/${userId}/reset`, { reason }),
  );

export const getQuizResultsApi = (id: string) =>
  call<QuizResultRow[]>("Erro ao carregar os resultados.", () => api.get(`/quiz-campaigns/${id}/results`));

export const getQuizQuestionStatsApi = (id: string) =>
  call<QuizQuestionStat[]>("Erro ao carregar o acerto por pergunta.", () => api.get(`/quiz-campaigns/${id}/question-stats`));

export const getQuizAttemptApi = (id: string, attemptId: string) =>
  call<QuizAttemptDetail>("Erro ao carregar as respostas.", () => api.get(`/quiz-campaigns/${id}/attempts/${attemptId}`));

export const exportQuizResultsApi = (id: string) =>
  call<Blob>("Erro ao exportar os resultados.", () => api.get(`/quiz-campaigns/${id}/results/export`, { responseType: "blob" }));

// ---------------------------------------------------------------------------
// Público (sem login)
// ---------------------------------------------------------------------------

export const getQuizPublicInfoApi = async (slug: string): Promise<QuizPublicInfo> => {
  try {
    const { data } = await api.get<QuizPublicInfo>(`/public/quiz/${encodeURIComponent(slug)}`);
    return data;
  } catch (error) {
    return throwPublicError(error, "Não foi possível abrir o quiz.");
  }
};

export const startQuizApi = async (slug: string, cpf: string): Promise<QuizStartResult> => {
  try {
    const { data } = await api.post<QuizStartResult>(`/public/quiz/${encodeURIComponent(slug)}/start`, { cpf });
    return data;
  } catch (error) {
    return throwPublicError(error, "Não foi possível começar o quiz.");
  }
};

export const submitQuizApi = async (
  slug: string,
  attemptToken: string,
  answers: Array<{ questionId: string; optionIds: string[] }>,
): Promise<QuizSubmitResult> => {
  try {
    const { data } = await api.post<QuizSubmitResult>(`/public/quiz/${encodeURIComponent(slug)}/submit`, { attemptToken, answers });
    return data;
  } catch (error) {
    return throwPublicError(error, "Não foi possível enviar as respostas.");
  }
};

// ---------------------------------------------------------------------------
// Formatação
// ---------------------------------------------------------------------------

export const QUESTION_TYPE_LABEL: Record<QuizQuestionType, string> = {
  SINGLE_CHOICE: "Única escolha",
  MULTI_CHOICE: "Múltipla escolha",
  TRUE_FALSE: "Verdadeiro ou falso",
};

export const CAMPAIGN_STATUS_LABEL: Record<QuizCampaignStatus, string> = {
  AGENDADA: "Agendada",
  ABERTA: "Aberta",
  ENCERRADA: "Encerrada",
};

export const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

export const fmtScore = (value: number | null | undefined) =>
  value === null || value === undefined ? "—" : `${value.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;

/** Valor de `<input type="datetime-local">` (hora local) a partir de ISO. */
export const toLocalInput = (iso: string) => {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const quizPublicUrl = (slug: string) => `${window.location.origin}/quiz/${slug}`;
